import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { useProject } from '../../context/ProjectContext';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { DataGrid } from '../Common/DataGrid';
import { ElectronicSign } from '../Common/ElectronicSign';
import { onboarding as onboardingMessages, common as commonMessages } from '../../utils/validationMessages';
import './SiteList.css';
import { pickTherapeuticArea } from '../../utils/therapeuticArea';

// Max site visits allowed per frequency (Site OnBoarding validation)
const MAX_VISITS_BY_FREQUENCY = {
  '15 Days': 15,
  'Weekly': 7,
  'Monthly': 28,
  'Quarterly': 112,
};

const getMaxVisitsForFrequency = (frequency) => {
  if (!(frequency || '').trim()) return null;
  const key = Object.keys(MAX_VISITS_BY_FREQUENCY).find(
    (k) => k.toLowerCase() === (frequency || '').trim().toLowerCase()
  );
  return key != null ? MAX_VISITS_BY_FREQUENCY[key] : null;
};

/** API sends messageKey (e.g. ssvIncomplete) — resolve copy from onboarding in validationMessages.js */
const messageFromOnboardingApi = (data, messages) => {
  const k = data?.messageKey;
  if (k && typeof messages[k] === 'string') return messages[k];
  return null;
};

const SiteList = () => {
  const { currentProject: contextProject } = useProject();
  const currentProject = contextProject && contextProject !== 'all' ? contextProject : null;
  const [profilePermissions, setProfilePermissions] = useState({});
  const isViewerProfile = profilePermissions.isViewOnly === 1 || profilePermissions.IsViewOnly === 1;
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSites, setSelectedSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showOnboardESignModal, setShowOnboardESignModal] = useState(false);
  // Per-row validation errors: { [siteId]: { siteCode?, frequency?, siteVisits? } }
  const [rowErrors, setRowErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [ssvChecking, setSsvChecking] = useState(false);
  const eSignRemarkRef = useRef('');

  const isSiteOnboarded = (site) => Boolean(site?.isOnboarded);

  /** Pending: lock only on open SSV. Onboarded: lock after any non-cancelled visit is scheduled. */
  const isSiteLocked = (site) =>
    Boolean(site?.isLockedAfterSiv || site?.isLockedOpenSsv || (site?.isOnboarded && site?.hasScheduledVisit));

  /** Prefer SSV-open copy when any SSV is not stage 9999; otherwise SIV scheduled lock. */
  const onboardingRowLockMessage = (site) => {
    if (!site) return '';
    if (site.isLockedOpenSsv) return onboardingMessages.ssvIncomplete;
    if (site.isLockedAfterSiv) return onboardingMessages.sivScheduledLock;
    return '';
  };

  useEffect(() => {
    fetchSites();
  }, [currentProject]);

  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) setProfilePermissions(res.data);
      })
      .catch(() => {
        if (!cancelled) setProfilePermissions({});
      });
    return () => { cancelled = true; };
  }, [currentProject]);

  const fetchSites = async () => {
    if (!currentProject) {
      setSites([]);
      setSelectedSites([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/sites/onboarding?project=${currentProject}`);
      const rows = response.data || [];
      const nextSites = rows.map((site) => ({
          ...site,
          therapeuticArea: pickTherapeuticArea(site) || site.therapeuticArea || '',
        }));
      setSites(nextSites);
      setSelectedSites((prev) => prev.filter((id) => !nextSites.some((site) => String(site.siteId) === String(id) && isSiteLocked(site))));
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to load sites';
      setError(errorMessage);
      console.error('Error fetching sites:', err);
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSite = (siteId, checked) => {
    const site = sites.find((s) => String(s.siteId) === String(siteId));
    if (site && isSiteLocked(site)) return;
    if (checked) {
      setSelectedSites(prev => [...prev, siteId]);
    } else {
      setSelectedSites(prev => prev.filter(id => id !== siteId));
    }
  };


  const handleSiteFieldChange = (siteId, field, value) => {
    const site = sites.find((s) => String(s.siteId) === String(siteId));
    if (site && isSiteLocked(site)) return;
    // Site Visits: only positive integers — allow digits only (no minus, decimal, letters or special chars)
    if (field === 'siteVisits') {
      value = String(value).replace(/\D/g, '');
    }
    setSites(prev => prev.map(site => 
      site.siteId === siteId ? { ...site, [field]: value } : site
    ));
    setRowErrors(prev => {
      const next = { ...prev };
      if (next[siteId]) {
        next[siteId] = { ...next[siteId], [field]: '' };
        if (Object.values(next[siteId]).every(v => !v)) delete next[siteId];
      }
      return next;
    });
  };

  const validateSelectedSites = () => {
    const errs = {};
    const selected = sites.filter(site => selectedSites.includes(site.siteId));
    const normalizeCode = (v) => (v || '').toString().trim().toLowerCase();
    // Compare against all loaded rows so a selected row cannot reuse another site's code.
    const allCodes = sites
      .map((s) => ({ siteId: s.siteId, code: normalizeCode(s.siteCode) }))
      .filter((x) => x.code);
    for (const site of selected) {
      const id = site.siteId;
      errs[id] = {};
      const siteCodeNorm = normalizeCode(site.siteCode);
      if (!siteCodeNorm) errs[id].siteCode = onboardingMessages.siteCodeRequired;
      if (!(site.frequency || '').trim()) errs[id].frequency = onboardingMessages.frequencyRequired;
      const sv = (site.siteVisits || '').toString().trim();
      if (!sv) errs[id].siteVisits = onboardingMessages.siteVisitsRequired;
      else if (!/^\d+$/.test(sv)) errs[id].siteVisits = onboardingMessages.siteVisitsPositiveInteger;
      else {
        const num = parseInt(sv, 10);
        if (Number.isNaN(num) || num < 1) errs[id].siteVisits = onboardingMessages.siteVisitsMin;
        else {
          const maxForFreq = getMaxVisitsForFrequency(site.frequency);
          if (maxForFreq != null && num > maxForFreq) {
            errs[id].siteVisits = onboardingMessages.siteVisitsMaxForFrequency
              .replace('{max}', String(maxForFreq))
              .replace('{frequency}', (site.frequency || '').trim() || 'selected frequency');
          }
        }
      }
      if (!errs[id].siteCode && siteCodeNorm) {
        const duplicateExists = allCodes.some((x) => String(x.siteId) !== String(id) && x.code === siteCodeNorm);
        if (duplicateExists) errs[id].siteCode = onboardingMessages.siteCodeDuplicate;
      }
      if (Object.keys(errs[id]).length === 0) delete errs[id];
    }
    setRowErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const doOnboard = async (remark) => {
    const onboardingData = {
      projectCode: currentProject,
      sites: sites
        .filter(site => selectedSites.includes(site.siteId))
        .map(site => ({
          siteId: site.siteId,
          siteCode: (site.siteCode || '').trim(),
          frequency: site.frequency || '',
          siteVisits: site.siteVisits || ''
        }))
    };
    if (remark != null && typeof remark === 'string' && remark.trim() !== '') {
      onboardingData.remark = remark.trim();
    }
    const response = await api.post('/sites/onboard', onboardingData);
    setError(null);
    setToast({
      type: 'success',
      message: response.data?.message || commonMessages.authSuccess,
    });
    try {
      await fetchSites();
    } catch (refreshErr) {
      console.error('Error refreshing sites after onboarding:', refreshErr);
    }
    setSelectedSites([]);
  };

  const handleOnboard = async () => {
    if (selectedSites.length === 0) {
      setToast({ type: 'error', message: onboardingMessages.selectOneSite });
      return;
    }
    if (!currentProject) {
      setError('No project selected. Please select a project first.');
      return;
    }
    setError(null);
    if (!validateSelectedSites()) return;

    setSsvChecking(true);
    try {
      await api.post('/sites/onboarding/validate-ssv', {
        projectCode: currentProject,
        siteIds: selectedSites.map((id) => String(id)),
      });
      setShowOnboardESignModal(true);
    } catch (err) {
      const payload = err.response?.data;
      const errorMessage =
        messageFromOnboardingApi(payload, onboardingMessages) ||
        payload?.message ||
        payload?.error ||
        err.message ||
        onboardingMessages.ssvIncomplete;
      setError(errorMessage);
      setToast({ type: 'error', message: errorMessage });
    } finally {
      setSsvChecking(false);
    }
  };

  const runAfterOnboardESign = (remarkFromESign) => {
    setShowOnboardESignModal(false);
    setSaving(true);
    setError(null);
    setRowErrors({});
    doOnboard(remarkFromESign).catch((err) => {
      const payload = err.response?.data;
      const errorMessage = messageFromOnboardingApi(payload, onboardingMessages) ||
                          payload?.message ||
                          payload?.error ||
                          err.message ||
                          'Failed to onboard sites';
      const isSiteCodeDuplicate =
        typeof errorMessage === 'string' &&
        errorMessage.toLowerCase().includes('already exists');
      setError(errorMessage);
      if (isSiteCodeDuplicate) {
        setRowErrors(prev => {
          const next = { ...prev };
          const failingSiteIdRaw = payload?.siteId;
          const failingSiteId = failingSiteIdRaw != null ? String(failingSiteIdRaw) : '';
          const selectedRows = sites.filter(s => selectedSites.includes(s.siteId));
          const targetSite = selectedRows.find((s) => String(s.siteId) === failingSiteId);
          if (targetSite) {
            next[targetSite.siteId] = { ...(next[targetSite.siteId] || {}), siteCode: onboardingMessages.siteCodeDuplicate };
          } else {
            // Fallback for legacy payloads that do not return a specific site id.
            selectedRows.forEach((s) => {
              next[s.siteId] = { ...(next[s.siteId] || {}), siteCode: onboardingMessages.siteCodeDuplicate };
            });
          }
          return next;
        });
      }
      if (!isSiteCodeDuplicate) {
        setToast({ type: 'error', message: errorMessage });
      }
      console.error('Error onboarding sites:', err);
    }).finally(() => setSaving(false));
  };

  if (loading) return <Spinner />;

  return (
    <div className="site-onboarding">
      {error && <Alert type="error" message={error} />}

      <div className="card mt-lg">
        <DataGrid
          data={sites}
          searchPlaceholder=""
          searchKeys={['siteNo', 'siteName', 'siteCode', 'vSiteCode', 'SiteCode', 'piName', 'sitedescription', 'city', 'therapeuticArea', 'disease', 'frequency', 'siteVisits']}
          defaultPageSize={10}
          emptyMessage="No sites available for onboarding."
          tableClassName="sites-onboarding-table"
          columns={[
            { key: 'select', label: 'Select' },
            { key: 'siteCode', label: 'Site' },
            { key: 'piName', label: 'PI Name' },
            { key: 'siteName', label: 'Site Name' },
            { key: 'therapeuticArea', label: 'Therapeutic Area' },
            { key: 'disease', label: 'Disease' },
            { key: 'frequency', label: 'Frequency' },
            { key: 'siteVisits', label: 'Site Visits' },
          ]}
          renderRow={(site) => {
            const onboarded = isSiteOnboarded(site);
            const locked = isSiteLocked(site);
            const lockMsg = onboardingRowLockMessage(site);
            const lockTitle = locked ? lockMsg : undefined;
            const fieldsDisabled = isViewerProfile || locked;
            const siteCodeDisabled = fieldsDisabled || onboarded;
            return (
            <tr key={site.siteId} className={onboarded ? 'site-onboarding-row-onboarded' : undefined}>
              <td>
                {locked ? (
                  <span className="site-onboarding-no-select" title={lockTitle || 'Locked'}>—</span>
                ) : (
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={selectedSites.includes(site.siteId)}
                    onChange={(e) => handleSelectSite(site.siteId, e.target.checked)}
                    disabled={isViewerProfile}
                    title={onboarded ? 'Update onboarded site details' : undefined}
                  />
                )}
              </td>
              <td>
                <input
                  type="text"
                  className="form-input form-input-sm"
                  value={site.siteCode || ''}
                  onChange={(e) => handleSiteFieldChange(site.siteId, 'siteCode', e.target.value)}
                  placeholder="Enter Site"
                  disabled={siteCodeDisabled}
                  title={lockTitle || (onboarded ? 'Site is fixed after onboarding' : undefined)}
                  readOnly={locked || onboarded}
                />
                {rowErrors[site.siteId]?.siteCode && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '11px', marginTop: '2px' }}>{rowErrors[site.siteId].siteCode}</div>
                )}
                {onboarded && (
                  <div className="field-error" style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>Onboarded</div>
                )}
                {locked && lockMsg && (
                  <div className="field-error" style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>{lockMsg}</div>
                )}
              </td>
              <td>{site.piName || site.investigator || ''}</td>
              <td>{site.siteName || site.sitedescription || ''}</td>
              <td>{site.therapeuticArea || ''}</td>
              <td>{site.disease || ''}</td>
              <td>
                <select
                  className="form-select form-select-sm"
                  value={site.frequency || ''}
                  onChange={(e) => handleSiteFieldChange(site.siteId, 'frequency', e.target.value)}
                  disabled={fieldsDisabled}
                  title={lockTitle}
                >
                  <option value="">Select Frequency</option>
                  <option value="15 Days">Fortnightly</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                </select>
                {rowErrors[site.siteId]?.frequency && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '11px', marginTop: '2px' }}>{rowErrors[site.siteId].frequency}</div>
                )}
              </td>
              <td>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  className="form-input form-input-sm"
                  value={site.siteVisits || ''}
                  onChange={(e) => handleSiteFieldChange(site.siteId, 'siteVisits', e.target.value)}
                  placeholder={getMaxVisitsForFrequency(site.frequency) != null ? `Max ${getMaxVisitsForFrequency(site.frequency)}` : ''}
                  title={lockTitle || (getMaxVisitsForFrequency(site.frequency) != null ? `Maximum ${getMaxVisitsForFrequency(site.frequency)} visits for ${(site.frequency || '').trim()}` : '')}
                  disabled={fieldsDisabled}
                  readOnly={locked}
                />
                {rowErrors[site.siteId]?.siteVisits && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '11px', marginTop: '2px' }}>{rowErrors[site.siteId].siteVisits}</div>
                )}
              </td>
            </tr>
          )}}
        />
      </div>

      <br />
      <div className="flex-between gap-md mb-3">
        <h3></h3>
        <div className="site-onboard-actions">
          {!isViewerProfile && (
            <button
              className="btn btn-primary"
              onClick={handleOnboard}
              disabled={saving || ssvChecking || !currentProject}
              title={!currentProject ? 'Select a project first' : selectedSites.length === 0 ? 'Select at least one site' : 'Onboard selected sites'}
            >
              {saving ? 'Onboarding...' : ssvChecking ? 'Checking SSV...' : 'Onboard'}
            </button>
          )}
        </div>
        <h3></h3>
      </div>
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
      <ElectronicSign
        show={showOnboardESignModal}
        onClose={() => setShowOnboardESignModal(false)}
        onAuthSuccess={(remark) => { eSignRemarkRef.current = (remark != null && typeof remark === 'string') ? remark.trim() : ''; }}
        onSave={() => runAfterOnboardESign(eSignRemarkRef.current)}
      />
    </div>
  );
};

export default SiteList;
